import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { KeyRound } from 'lucide-react';
import useAuthService from '../../services/authService';
import PasswordRequirementsList from '../Users/PasswordRequirementsList';
import {
  evaluatePasswordRules,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  passwordRulesAllSatisfied,
} from '../../utils/passwordRules';
import './styles/ChangePasswordModal.css';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { changePassword, fetching } = useAuthService();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleCancel();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(false);
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const getBackendError = (err: any): string => {
    const errors = err?.errors;
    if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
      const parts = Object.values(errors)
        .flat()
        .map((x) => (Array.isArray(x) ? x.join(' ') : String(x)))
        .filter(Boolean);
      if (parts.length) return parts.join(' ');
    }
    const msg = err?.message || err?.errors?.current_password?.[0] || err?.errors?.new_password?.[0];
    if (Array.isArray(msg)) return msg[0];
    return msg || 'Error al cambiar la contraseña.';
  };

  const newRulesOk = useMemo(() => passwordRulesAllSatisfied(newPassword), [newPassword]);
  const confirmHasText = confirmPassword.length > 0;
  const passwordsMismatch = confirmHasText && newPassword !== confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!currentPassword.trim()) {
      setError('Ingresa tu contraseña actual.');
      return;
    }
    if (!newPassword.trim()) {
      setError('Ingresa la nueva contraseña.');
      return;
    }
    if (newPassword.length > PASSWORD_MAX_LENGTH) {
      setError(`La contraseña no puede superar ${PASSWORD_MAX_LENGTH} caracteres.`);
      return;
    }
    if (!newRulesOk) {
      setError(
        `La contraseña debe tener al menos ${PASSWORD_MIN_LENGTH} caracteres, incluir letras, números y al menos un carácter especial.`
      );
      return;
    }
    if (!confirmPassword.trim()) {
      setError('Confirma la nueva contraseña.');
      return;
    }
    if (newPassword !== confirmPassword) {
      const msg = 'Las contraseñas no coinciden.';
      window.alert(msg);
      setError(null);
      return;
    }

    try {
      await changePassword(currentPassword, newPassword, {
        onSuccess: () => {
          setSuccess(true);
          setTimeout(() => {
            resetForm();
            onClose();
            onSuccess?.();
          }, 1500);
        },
        onError: (r: any) => {
          setError(getBackendError(r));
        },
      });
    } catch (err: any) {
      setError(getBackendError(err));
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="change-password-overlay" onClick={handleCancel}>
      <div
        className="change-password-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="change-password-icon">
          <div className="change-password-icon-circle">
            <KeyRound size={32} />
          </div>
        </div>
        <h3 className="change-password-title">Cambiar contraseña</h3>
        <p className="change-password-message">
          Ingresa tu contraseña actual y la nueva. Mínimo {PASSWORD_MIN_LENGTH} caracteres, letras, números y un carácter
          especial; máximo {PASSWORD_MAX_LENGTH} caracteres.
        </p>

        {success ? (
          <p className="change-password-success">Contraseña actualizada correctamente.</p>
        ) : (
          <form onSubmit={handleSubmit} className="change-password-form">
            <div className="change-password-field">
              <label htmlFor="current_password">Contraseña actual</label>
              <input
                id="current_password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="change-password-input"
                placeholder="Contraseña actual"
                autoComplete="current-password"
              />
            </div>
            <div className="change-password-field">
              <label htmlFor="new_password">Nueva contraseña</label>
              <input
                id="new_password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="change-password-input"
                placeholder={`Mín. ${PASSWORD_MIN_LENGTH} caracteres, máx. ${PASSWORD_MAX_LENGTH}`}
                maxLength={PASSWORD_MAX_LENGTH}
                autoComplete="new-password"
              />
              {newPassword.length > 0 && <PasswordRequirementsList password={newPassword} />}
            </div>
            <div className="change-password-field">
              <label htmlFor="confirm_password">Confirmar nueva contraseña</label>
              <input
                id="confirm_password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="change-password-input"
                placeholder="Repetir nueva contraseña"
                autoComplete="new-password"
              />
              {passwordsMismatch && (
                <div className="change-password-error change-password-error--match" role="alert">
                  Las contraseñas no coinciden.
                </div>
              )}
            </div>
            {error && (
              <div className="change-password-error" role="alert">
                {error}
              </div>
            )}
            <div className="change-password-actions">
              <button
                type="button"
                className="change-password-btn-cancel"
                onClick={handleCancel}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="change-password-btn-submit"
                disabled={
                  !!fetching.changePassword ||
                  !newRulesOk ||
                  !evaluatePasswordRules(newPassword).withinMaxLength ||
                  !confirmPassword.trim()
                }
              >
                {fetching.changePassword ? 'Guardando…' : 'Actualizar contraseña'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ChangePasswordModal;
